import { useMemo } from 'react';

import { scaleLinear } from 'd3-scale';
import { format, getYear } from 'date-fns';
import { useAtomValue } from 'jotai';
import { useNavigate } from 'react-router';
import {
    CartesianGrid,
    Dot,
    Legend,
    Line,
    LineChart,
    ReferenceLine,
    ResponsiveContainer,
    XAxis,
    YAxis,
} from 'recharts';

import { getDisplayUnitAndSymbol, simpleConvert, Unit } from '@core';
import type { GoalReport, UserPreferredUnit } from '@data';
import { useLayoutGoalReport } from '@queries';
import { activityUnitAtom, layoutAtom } from '@store';
import { Box, colors, Panel, Spinner, Stack, StatusBox, u } from '@ui';

import { useGoalReportContext } from '../../GoalReportContext';

import * as css from './GoalReportCharts.css';
import { convertFiltersToGoalReportParams } from './goalReportCharts.utils';

import type { Payload } from 'recharts/types/component/DefaultLegendContent';

const DATE_FORMAT = 'dd/MM/yyyy';
const SHORT_DATE_FORMAT = 'dd/MM';

type SwingPeriod = {
    swings: number;
    date: string;
    index: number;
    last: boolean;
    shouldOffset: boolean;
    sessionDate: string;
};

export const GoalReportCharts = () => {
    const { filters, layoutId, customRange } = useGoalReportContext();
    const { from, to } = customRange;

    const {
        data: goalReports,
        error: goalReportError,
        isLoading: goalReportIsLoading,
    } = useLayoutGoalReport(convertFiltersToGoalReportParams({ ...filters, from, to, layoutId }));
    const activityUnit = useAtomValue(activityUnitAtom);

    if (goalReportError) {
        return (
            <StatusBox
                title="Error generating goal report"
                description={'Unknown error occurred while generating report'}
                status="error"
            />
        );
    }

    if (goalReportIsLoading) {
        return (
            <Box display="flex" justifyContent="center" alignItems="center" height="100%" flex={1}>
                <Spinner size="l" />
            </Box>
        );
    }
    const noSwings = !goalReports?.some((report) => (report.swings ?? []).length > 0);
    if (noSwings) {
        return (
            <StatusBox
                status="info"
                title="No swings match your current filters"
                description="Adjust the date range or reset filters to include swings"
                action={{ label: 'Reset filters', handleAction: () => {} }}
            />
        );
    }
    return (
        <Stack marginBlockEnd={u(2)} paddingBlockEnd={u(2)} gap={u(2)} flex={1} className={css.overflow}>
            {goalReports?.map((goalReport) => (
                <Panel
                    key={goalReport.parameterName}
                    icon="chart"
                    title={goalReport.parameterName.replace(/_/g, ' ')}
                    collapsible
                    initialState="open"
                    titleClassName={css.titleClassName}
                >
                    {goalReports ? (
                        <GoalReportChart
                            swings={goalReport.swings}
                            userPreferredUnit={activityUnit}
                            unit={goalReport.unit}
                            goalMax={goalReport.innerMax}
                            goalMin={goalReport.innerMin}
                        />
                    ) : (
                        <Spinner size="l" />
                    )}
                </Panel>
            ))}
        </Stack>
    );
};

type GoalReportChartProps = {
    swings: GoalReport['swings'];
    userPreferredUnit: UserPreferredUnit;
    unit: Unit;
    goalMax: number;
    goalMin: number;
};

const legendPayload: Payload[] = [
    { value: 'Goal Low', color: colors.orange[600], type: 'line' },
    { value: 'Goal High', color: colors.green[600], type: 'line' },
    { value: 'Value', color: colors.blue[600], type: 'line' },
    { value: 'Running Average (last 6)', color: colors.cyan[600], type: 'line' },
];

export const GoalReportChart = ({ swings, userPreferredUnit, unit, goalMax, goalMin }: GoalReportChartProps) => {
    const navigate = useNavigate();
    const { getFullActivityRoute } = useGoalReportContext();
    const {
        convertedGoalMax,
        convertedGoalMin,
        convertedSwings,
        symbol: displaySymbol,
    } = useMemo(() => {
        return {
            convertedSwings: swings
                ?.sort((a, b) => new Date(a.createdAt).getTime() - new Date(b.createdAt).getTime())
                .map((swing, i) => ({
                    index: i,
                    ...swing,
                    parameterValue: simpleConvert({ value: swing.parameterValue, unit, userPreferredUnit }),
                })),
            convertedGoalMax: simpleConvert({ value: goalMax, unit, userPreferredUnit }),
            convertedGoalMin: simpleConvert({ value: goalMin, unit, userPreferredUnit }),
            ...getDisplayUnitAndSymbol(unit, userPreferredUnit),
        };
    }, [swings, goalMax, unit, userPreferredUnit, goalMin]);

    const swingsWithRunningAverage = useMemo(() => {
        return convertedSwings?.map((swing, index, array) => {
            const startIndex = Math.max(0, index - 5);
            const lastSixSwings = array.slice(startIndex, index + 1);
            const sum = lastSixSwings.reduce((acc, current) => acc + current.parameterValue, 0);
            const average = sum / lastSixSwings.length;
            return {
                ...swing,
                runningAverage: average,
            };
        });
    }, [convertedSwings]);

    const yAxisScale = useMemo(() => {
        const allValues =
            swingsWithRunningAverage?.flatMap((dataPoint) => [dataPoint.parameterValue, dataPoint.runningAverage]) ||
            [];

        allValues.push(convertedGoalMax, convertedGoalMin);

        if (allValues.length === 0) {
            return null;
        }

        const min = Math.min(...allValues);
        const max = Math.max(...allValues);
        const range = (max - min) * 0.05;
        return scaleLinear()
            .domain([min - range, max + range])
            .nice();
    }, [swingsWithRunningAverage, convertedGoalMax, convertedGoalMin]);

    const timeSteps = useMemo(() => {
        const numberOfSwings = convertedSwings?.length;
        if (!numberOfSwings) return [];
        const years: Record<number, boolean> = {};
        return convertedSwings.reduce((acc, swing, index) => {
            const date = format(swing.createdAt, DATE_FORMAT);
            const year = getYear(swing.createdAt);
            const previous = acc.at(-1);
            if (previous?.sessionDate !== date) {
                const swingPeriod = {
                    sessionDate: date,
                    date: years[year] ? format(swing.createdAt, SHORT_DATE_FORMAT) : date,
                    swings: 1,
                    index,
                    last: index === convertedSwings.length - 1,
                    shouldOffset: typeof previous?.shouldOffset === 'undefined' ? false : !previous?.shouldOffset,
                };
                years[year] = true;
                return acc.concat(swingPeriod);
            }
            const { swings } = previous;
            const newSwingsCount = swings + 1;
            const swingPeriod = {
                ...previous,
                swings: newSwingsCount,
                shouldOffset: !previous.shouldOffset && newSwingsCount < 2,
            };
            return acc.slice(0, -1).concat(swingPeriod);
        }, [] as SwingPeriod[]);
    }, [convertedSwings]);

    if (!yAxisScale) {
        return null;
    }

    return (
        <ResponsiveContainer width="100%" height={300}>
            <LineChart data={swingsWithRunningAverage} margin={{ top: 5, right: 30, left: 20, bottom: 5 }}>
                <CartesianGrid strokeDasharray="3 3" />
                <XAxis
                    dataKey="index"
                    tickFormatter={(_, i) => `${i + 1}`}
                    stroke={colors.bluegray[400]}
                    fontSize={10}
                    fontWeight={500}
                />
                <ReferenceLine y={convertedGoalMax} stroke={colors.green[600]} name="Goal Low" />
                <ReferenceLine y={convertedGoalMin} stroke={colors.orange[600]} name="Goal High" />
                <ReferenceLine y={0} stroke={colors.bluegray[600]} />
                {timeSteps?.map((step) => (
                    <ReferenceLine
                        key={step.date}
                        x={step.index}
                        stroke={colors.bluegray[600]}
                        label={<CustomLabel date={step.date} shouldOffset={step.shouldOffset} last={step.last} />}
                    />
                ))}
                <YAxis
                    tickFormatter={(value: number) => `${value}${displaySymbol}`}
                    domain={yAxisScale.domain()}
                    ticks={yAxisScale.ticks(4)}
                    stroke={colors.bluegray[400]}
                    fontSize={10}
                    fontWeight={500}
                />

                <Legend payload={legendPayload} />
                <Line
                    isAnimationActive={false}
                    dataKey="runningAverage"
                    stroke={colors.cyan[600]}
                    name="Running Average (Last 6)"
                />
                <Line
                    isAnimationActive={false}
                    dataKey="parameterValue"
                    stroke={colors.blue[600]}
                    strokeWidth={2}
                    dot={(props) => (
                        <Dot
                            {...props}
                            r={5}
                            cursor="pointer"
                            onClick={() =>
                                navigate(getFullActivityRoute(props.payload.activityId, props.payload.swingUuid))
                            }
                        />
                    )}
                    name="Swings"
                />
            </LineChart>
        </ResponsiveContainer>
    );
};

// Recharts types are not explicit here so this is quick and dirty way to accomplish what we need
// viewBox is passed by recharts but rest by us
type CustomLabelProps = {
    viewBox?: { x: number };
    date: string;
    shouldOffset: boolean;
    last: boolean;
};
const CustomLabel = ({ viewBox, date, last, shouldOffset }: CustomLabelProps) => {
    return (
        <text
            x={viewBox?.x}
            y={shouldOffset ? 12 : 0}
            dy={0}
            textAnchor={last ? 'end' : 'start'}
            stroke={colors.bluegray[500]}
            fontSize={10}
            fontWeight={200}
        >
            {date}
        </text>
    );
};
